/**
 * Form Validation Hooks
 * React Hook Form integration with Zod validation schemas
 */

'use client';

import { useForm, UseFormReturn, FieldValues, UseFormProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useCallback, useEffect } from 'react';

// =============================================================================
// FORM VALIDATION HOOK
// =============================================================================

/**
 * Enhanced useForm hook with Zod validation
 */
export function useFormValidation<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any
>(
  schema: z.ZodSchema<TFieldValues>,
  options?: Omit<UseFormProps<TFieldValues, TContext>, 'resolver'>
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const form = useForm<TFieldValues, TContext>({
    ...options,
    resolver: zodResolver(schema as any),
  });

  const resetSubmitState = useCallback(() => {
    setIsSubmitting(false);
    setSubmitError(null);
    setSubmitSuccess(false);
  }, []);

  // Reset submit state when form values change
  useEffect(() => {
    const subscription = form.watch(() => {
      if (submitError || submitSuccess) {
        setSubmitError(null);
        setSubmitSuccess(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, submitError, submitSuccess]);

  return {
    ...form,
    isSubmitting,
    submitError,
    submitSuccess,
    resetSubmitState,
  };
}

// =============================================================================
// ASYNC FORM SUBMISSION HOOK
// =============================================================================

/**
 * Hook for handling async form submissions with error handling
 */
export function useAsyncFormSubmit<TFieldValues extends FieldValues>(
  onSubmit: (data: TFieldValues) => Promise<void>,
  options?: {
    onSuccess?: (data: TFieldValues) => void;
    onError?: (error: Error, data: TFieldValues) => void;
    resetOnSuccess?: boolean;
  }
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = useCallback(
    async (data: TFieldValues, form?: UseFormReturn<TFieldValues>) => {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      try {
        await onSubmit(data);
        setSubmitSuccess(true);
        options?.onSuccess?.(data);

        if (options?.resetOnSuccess && form) {
          form.reset();
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An error occurred';
        setSubmitError(errorMessage);
        options?.onError?.(error as Error, data);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, options]
  );

  const resetSubmitState = useCallback(() => {
    setIsSubmitting(false);
    setSubmitError(null);
    setSubmitSuccess(false);
  }, []);

  return {
    handleSubmit,
    isSubmitting,
    submitError,
    submitSuccess,
    resetSubmitState,
  };
}

// =============================================================================
// FIELD VALIDATION HOOK
// =============================================================================

/**
 * Hook for real-time field validation
 */
export function useFieldValidation<T>(
  schema: z.ZodSchema<T>,
  debounceMs: number = 300
) {
  const [value, setValue] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);

  // Debounced validation
  useEffect(() => {
    if (value === undefined) return;

    setIsValidating(true);
    const timeoutId = setTimeout(() => {
      const result = schema.safeParse(value);

      if (result.success) {
        setError(null);
        setIsValid(true);
      } else {
        setError(result.error.issues[0]?.message || 'Validation failed');
        setIsValid(false);
      }

      setIsValidating(false);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [value, schema, debounceMs]);

  const validate = useCallback((newValue: T) => {
    setValue(newValue);
  }, []);

  const reset = useCallback(() => {
    setValue(undefined);
    setError(null);
    setIsValid(false);
    setIsValidating(false);
  }, []);

  return {
    validate,
    error,
    isValid,
    isValidating,
    reset,
  };
}

// =============================================================================
// MULTI-STEP FORM HOOK
// =============================================================================

/**
 * Hook for managing multi-step forms with validation
 */
export function useMultiStepForm<T extends Record<string, any>>(
  steps: Array<{
    name: string;
    schema: z.ZodSchema<any>;
    fields: (keyof T)[];
  }>,
  initialData?: Partial<T>
) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<T>>(initialData || {});
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [stepErrors, setStepErrors] = useState<Record<number, string[]>>({});

  const currentStepConfig = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  // Validate current step
  const validateCurrentStep = useCallback(() => {
    if (!currentStepConfig) return false;

    const stepData: any = {};
    currentStepConfig.fields.forEach((field) => {
      stepData[field] = formData[field];
    });

    const result = currentStepConfig.schema.safeParse(stepData);

    if (result.success) {
      setStepErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[currentStep];
        return newErrors;
      });
      return true;
    } else {
      const errors = result.error.issues.map((err) => err.message);
      setStepErrors((prev) => ({
        ...prev,
        [currentStep]: errors,
      }));
      return false;
    }
  }, [currentStep, currentStepConfig, formData]);

  // Go to next step
  const nextStep = useCallback(() => {
    if (validateCurrentStep() && !isLastStep) {
      setCompletedSteps((prev) => new Set(prev).add(currentStep));
      setCurrentStep((prev) => prev + 1);
    }
  }, [validateCurrentStep, isLastStep, currentStep]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [isFirstStep]);

  // Go to specific step
  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      // Validate all steps up to the target step
      let canNavigate = true;
      for (let i = 0; i < stepIndex; i++) {
        const stepConfig = steps[i];
        if (!stepConfig) {
          canNavigate = false;
          break;
        }
        const stepData: any = {};
        stepConfig.fields.forEach((field) => {
          stepData[field] = formData[field];
        });

        const result = stepConfig.schema.safeParse(stepData);
        if (!result.success) {
          canNavigate = false;
          break;
        }
      }

      if (canNavigate) {
        setCurrentStep(stepIndex);
      }
    }
  }, [steps, formData]);

  // Update form data
  const updateFormData = useCallback((data: Partial<T>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  // Reset form
  const reset = useCallback(() => {
    setCurrentStep(0);
    setFormData(initialData || {});
    setCompletedSteps(new Set());
    setStepErrors({});
  }, [initialData]);

  // Validate entire form
  const validateForm = useCallback(() => {
    // Validate each step individually since we can't easily merge schemas in Zod v4
    for (const step of steps) {
      const stepData: any = {};
      step.fields.forEach((field) => {
        stepData[field] = formData[field];
      });

      const result = step.schema.safeParse(stepData);
      if (!result.success) {
        return false;
      }
    }
    return true;
  }, [steps, formData]);

  return {
    currentStep,
    currentStepConfig,
    formData,
    completedSteps,
    stepErrors,
    isFirstStep,
    isLastStep,
    nextStep,
    prevStep,
    goToStep,
    updateFormData,
    validateCurrentStep,
    validateForm,
    reset,
    progress: ((currentStep + 1) / steps.length) * 100,
  };
}

// =============================================================================
// FORM PERSISTENCE HOOK
// =============================================================================

/**
 * Hook for persisting form data to localStorage
 */
export function useFormPersistence<T extends FieldValues>(
  key: string,
  form: UseFormReturn<T>,
  options?: {
    exclude?: (keyof T)[];
    debounceMs?: number;
  }
) {
  const { exclude = [], debounceMs = 1000 } = options || {};

  // Load persisted data on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const data = JSON.parse(saved);
        // Only restore non-excluded fields
        const filteredData: any = {};
        Object.keys(data).forEach((field) => {
          if (!exclude.includes(field as keyof T)) {
            filteredData[field] = data[field];
          }
        });
        form.reset(filteredData);
      }
    } catch (error) {
      console.warn('Failed to load persisted form data:', error);
    }
  }, [key, form, exclude]);

  // Save form data on changes (debounced)
  useEffect(() => {
    const subscription = form.watch((data) => {
      const timeoutId = setTimeout(() => {
        try {
          // Exclude specified fields from persistence
          const dataToSave: any = {};
          Object.keys(data).forEach((field) => {
            if (!exclude.includes(field as keyof T)) {
              dataToSave[field] = data[field];
            }
          });
          localStorage.setItem(key, JSON.stringify(dataToSave));
        } catch (error) {
          console.warn('Failed to persist form data:', error);
        }
      }, debounceMs);

      return () => clearTimeout(timeoutId);
    });

    return () => subscription.unsubscribe();
  }, [key, form, exclude, debounceMs]);

  // Clear persisted data
  const clearPersistedData = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear persisted form data:', error);
    }
  }, [key]);

  return { clearPersistedData };
}

// =============================================================================
// CONDITIONAL VALIDATION HOOK
// =============================================================================

/**
 * Hook for conditional field validation
 */
export function useConditionalValidation<T>(
  condition: (formData: T) => boolean,
  schema: z.ZodSchema<T>,
  fallbackSchema?: z.ZodSchema<T>
) {
  const [currentSchema, setCurrentSchema] = useState<z.ZodSchema<T>>(schema);

  const updateSchema = useCallback(
    (formData: T) => {
      if (condition(formData)) {
        setCurrentSchema(schema);
      } else if (fallbackSchema) {
        setCurrentSchema(fallbackSchema);
      }
    },
    [condition, schema, fallbackSchema]
  );

  return {
    currentSchema,
    updateSchema,
  };
}

// =============================================================================
// EXPORT TYPES
// =============================================================================

export type FormValidationHook<T extends FieldValues> = ReturnType<
  typeof useFormValidation<T>
>;

export type AsyncFormSubmitHook<T extends FieldValues> = ReturnType<
  typeof useAsyncFormSubmit<T>
>;

export type FieldValidationHook<T> = ReturnType<typeof useFieldValidation<T>>;

export type MultiStepFormHook<T extends Record<string, any>> = ReturnType<
  typeof useMultiStepForm<T>
>;

export type FormPersistenceHook<T extends FieldValues> = ReturnType<
  typeof useFormPersistence<T>
>;

export type ConditionalValidationHook<T> = ReturnType<
  typeof useConditionalValidation<T>
>;
